<script setup lang="ts">
import { useCourseOutstandingWaivers } from '@/composables/queries/useCourseOutstandingWaivers';
import { useCourseOutstandingWaiverMutations } from '@/composables/mutations/tertiary/useCourseOutstandingWaiverMutations';
import { useStudents } from '@/composables/queries/useStudent';
import { useCourses } from '@/composables/queries/useCourses';
import { EnrollmentStatusEnum } from '@/types/composition-functions';
import { useConfirm } from 'primevue/useconfirm';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';

const confirm = useConfirm();
const { waivers, loading, refetch, setFilter, clearFilter } = useCourseOutstandingWaivers();
const { addWaiver, deleteWaiver, bulkDeleteWaivers, adding, deleting, bulkDeleting } = useCourseOutstandingWaiverMutations();

// Student search
const studentSearchFilters = ref({ first: 50, page: 1, filter: { search: '', status: EnrollmentStatusEnum.Enrolled } });
const { students: waiverStudents, loadStudents: loadWaiverStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: studentSearchFilters as never,
});
let sDebounce: ReturnType<typeof setTimeout> | null = null;
function onStudentFilter(event: { value: string }) {
  if (sDebounce) clearTimeout(sDebounce);
  sDebounce = setTimeout(() => {
    studentSearchFilters.value = { first: 50, page: 1, filter: { search: event.value, status: EnrollmentStatusEnum.Enrolled } };
    loadWaiverStudents();
  }, 300);
}
const studentOptions = computed(() =>
  waiverStudents.data.value.map((s) => ({ label: `${s.matric_no} - ${s.surname} ${s.first_name}`, value: s.id })),
);

// Course search
const { courses: waiverCourses, setFilter: setWaiverCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
let cDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (cDebounce) clearTimeout(cDebounce);
  cDebounce = setTimeout(() => { setWaiverCourseFilter({ query: event.value }); }, 300);
}
const courseOptions = computed(() =>
  waiverCourses.value.map((c: { id: string; course_code: string; course_title: string; unit: number }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit}u)`, value: c.id,
  })),
);

const selectedRows = ref<Array<Record<string, unknown>>>([]);

const filterQuery = ref('');
const filterCalendarId = ref<string | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function onFilterChange() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    setFilter({
      query: filterQuery.value || undefined,
      academic_calendar_id: filterCalendarId.value || undefined,
    });
  }, 400);
}

function resetFilters() {
  filterQuery.value = '';
  filterCalendarId.value = null;
  clearFilter();
}

const hasActiveFilters = computed(() => filterQuery.value || filterCalendarId.value);

const showAddDialog = ref(false);
const addForm = reactive({
  student_id: '',
  course_id: '',
  academic_calendar_id: '',
  reason: '',
});

function resetAddForm() {
  addForm.student_id = '';
  addForm.course_id = '';
  addForm.academic_calendar_id = '';
  addForm.reason = '';
}

async function handleAdd() {
  if (!addForm.student_id || !addForm.course_id || !addForm.academic_calendar_id) return;
  const result = await addWaiver({
    student_id: addForm.student_id,
    course_id: addForm.course_id,
    academic_calendar_id: addForm.academic_calendar_id,
    reason: addForm.reason || undefined,
  });
  if (result?.success) {
    showAddDialog.value = false;
    resetAddForm();
    await refetch();
  }
}

function handleBulkDelete() {
  confirm.require({
    message: `Are you sure you want to remove ${selectedRows.value.length} waiver(s)?`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      const ids = selectedRows.value.map((row) => row.id as string);
      const result = await bulkDeleteWaivers(ids);
      if (result?.success) {
        selectedRows.value = [];
        await refetch();
      }
    },
  });
}

async function handleDeleteRow(id: string) {
  const result = await deleteWaiver(id);
  if (result?.success) {
    await refetch();
  }
}

watch([filterQuery, filterCalendarId], () => onFilterChange());
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-2">
      <Button label="Add Waiver" icon="pi pi-plus" size="small" @click="showAddDialog = true" />
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Search (Matric / Course)</label>
        <InputText v-model="filterQuery" placeholder="Search..." size="small" class="w-full rounded-lg" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Academic Calendar</label>
        <AcademicCalendarSelect v-model="filterCalendarId" placeholder="All Calendars" class="w-full" />
      </div>
    </div>
    <div v-if="hasActiveFilters" class="flex justify-end">
      <Button label="Clear" icon="pi pi-times" size="small" text severity="secondary" @click="resetFilters" />
    </div>

    <div v-if="selectedRows.length > 0"
      class="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
      <span class="text-sm text-primary-700 font-medium">{{ selectedRows.length }} waiver(s) selected</span>
      <div class="flex gap-2">
        <Button label="Delete" icon="pi pi-trash" size="small" severity="danger" outlined
          @click="handleBulkDelete" :loading="bulkDeleting" />
        <Button label="Clear" icon="pi pi-times-circle" size="small" severity="secondary" text
          @click="selectedRows = []" />
      </div>
    </div>

    <DataTable
      size="small"
      :value="waivers"
      :loading="loading"
      v-model:selection="selectedRows"
      dataKey="id"
      stripedRows
      tableStyle="min-width: 50rem"
    >
      <template #empty>No temporary waivers found.</template>
      <Column selectionMode="multiple" headerStyle="width: 3rem" />
      <Column header="Student">
        <template #body="{ data }">
          <div>
            <div class="font-medium text-sm">{{ data.student?.surname }} {{ data.student?.first_name }}</div>
            <div class="text-xs text-gray-500">{{ data.student?.matric_no }}</div>
          </div>
        </template>
      </Column>
      <Column header="Course">
        <template #body="{ data }">
          <div>
            <div class="font-medium text-sm">{{ data.course?.course_code }}</div>
            <div class="text-xs text-gray-500">{{ data.course?.course_title }}</div>
          </div>
        </template>
      </Column>
      <Column header="Units">
        <template #body="{ data }">
          <span class="bg-gray-100 px-2 py-0.5 rounded text-xs font-bold">{{ data.course?.unit }}</span>
        </template>
      </Column>
      <Column header="Academic Calendar">
        <template #body="{ data }">
          <span class="text-sm">
            {{ data.academicCalendar?.session?.name }} — {{ data.academicCalendar?.termStructure?.name }}
          </span>
        </template>
      </Column>
      <Column header="Reason">
        <template #body="{ data }">
          <span class="text-sm text-gray-600">{{ data.reason || '—' }}</span>
        </template>
      </Column>
      <Column header="Actions" headerStyle="width: 5rem">
        <template #body="{ data }">
          <Button icon="pi pi-trash" size="small" severity="danger" text
            @click="handleDeleteRow(data.id)" :loading="deleting" />
        </template>
      </Column>
    </DataTable>

    <Dialog v-model:visible="showAddDialog" header="Add Temporary Waiver" modal class="w-full md:w-5/12">
      <div class="flex flex-col gap-4 py-2">
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Student *</label>
          <Select v-model="addForm.student_id" :options="studentOptions" optionLabel="label" optionValue="value"
            placeholder="Search student by matric or name..." filter @filter="onStudentFilter" showClear class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Course *</label>
          <Select v-model="addForm.course_id" :options="courseOptions" optionLabel="label" optionValue="value"
            placeholder="Search course by code or title..." filter @filter="onCourseFilter" showClear class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Academic Calendar</label>
          <AcademicCalendarSelect v-model="addForm.academic_calendar_id" class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Reason (optional)</label>
          <Textarea v-model="addForm.reason" placeholder="Reason for waiver..." rows="2" class="w-full" />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" text @click="showAddDialog = false" />
        <Button label="Add Waiver" icon="pi pi-check" @click="handleAdd" :loading="adding"
          :disabled="!addForm.student_id || !addForm.course_id || !addForm.academic_calendar_id" />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>
